import { Routes } from '@angular/router';
import { authGuard, adminClaimGuard, coachingGuard, curriculumEditGuard, landingGuard, orgCurriculumGuard, orgLibraryGuard, platformAdminGuard, staffGuard } from '@app/auth';
import { PlaceholderComponent } from './pages/placeholder/placeholder.component';
import { msaGate } from './auth/msa-gate';


/**
 * The Program Library and its program pages, inside an organization console
 * (`prefix` is the organization or one of its divisions). The program pages
 * are the same components VentureOff staff use under `/programs`.
 */
function programLibraryRoutes(prefix: string): Routes {
  const lib = `${prefix}/library`;
  return [
    { path: lib, title: 'Program Library', canActivate: [orgLibraryGuard], loadComponent: () => import('./pages/organizations/library/library.component').then(m => m.LibraryComponent) },
    { path: `${lib}/:programId/edit`, title: 'Program editor', canActivate: [orgCurriculumGuard], loadComponent: () => import('./pages/programs/editor/program-editor.component').then(m => m.ProgramEditorComponent) },
    { path: `${lib}/:programId/details`, title: 'Program details', canActivate: [orgCurriculumGuard], loadComponent: () => import('./pages/programs/form/program-form.component').then(m => m.ProgramFormComponent) },
    { path: `${lib}/:programId/version/:versionNumber`, title: 'Program version', canActivate: [orgCurriculumGuard], loadComponent: () => import('./pages/programs/version-preview/program-version-preview.component').then(m => m.ProgramVersionPreviewComponent) },
    { path: `${lib}/:programId`, title: 'Program versions', canActivate: [orgCurriculumGuard], loadComponent: () => import('./pages/programs/list/programs-list.component').then(m => m.ProgramsListComponent) },
  ];
}

export const routes: Routes = [
  {
    path: 'login',
    title: 'Sign in',
    loadComponent: () =>
      import('./pages/login/login.component').then((m) => m.LoginComponent),
  },
  {
    // The reset page itself lives in vo-user (one page for both apps).
    path: 'forgot-password',
    title: 'Reset your password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: 'register-organization',
    title: 'Register organization',
    loadComponent: () =>
      import('./pages/register-organization/register-organization.component').then(
        (m) => m.RegisterOrganizationComponent,
      ),
  },
  {
    path: 'verify-email/success',
    title: 'Email verified',
    loadComponent: () =>
      import('./pages/verify-email-success/verify-email-success.component').then(
        (m) => m.VerifyEmailSuccessComponent,
      ),
  },
  {
    path: 'create-organization',
    title: 'Create organization',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/create-organization/create-organization.component').then(
        (m) => m.CreateOrganizationComponent,
      ),
  },
  {
    // Outside the admin shell: the shell's own requests are the ones the API
    // refuses until two-step sign-in is set up (ADMIN_MFA_REQUIRED).
    path: 'two-step',
    title: 'Two-step sign-in',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/two-step/two-step.component').then((m) => m.TwoStepComponent),
  },
  {
    path: 'forbidden',
    title: 'Access denied',
    loadComponent: () =>
      import('./pages/forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
  },
  {
    path: '',
    canActivate: [authGuard, adminClaimGuard],
    // Owners sign their organization's MSA before using the admin.
    canActivateChild: [msaGate],
    loadComponent: () =>
      import('./layouts/admin-layout/admin-layout.component').then(
        (m) => m.AdminLayoutComponent,
      ),
    children: [
      {
        path: 'organizations/:orgId/sign-msa',
        title: 'Master services agreement',
        loadComponent: () => import('./pages/organizations/msa/sign-msa.component').then((m) => m.SignMsaComponent),
      },
      { path: '', pathMatch: 'full', canActivate: [landingGuard], children: [] },
      {
        path: 'choose-organization',
        title: 'Choose organization',
        loadComponent: () =>
          import('./pages/choose-organization/choose-organization.component').then((m) => m.ChooseOrganizationComponent),
      },

      // Platform-console destinations whose feature page isn't built yet. The
      // redesigned nav surfaces these (role-gated), so they resolve to a
      // "coming soon" placeholder rather than redirecting away.
      { path: 'overview', title: 'Overview', canActivate: [staffGuard('staff.orgs.read')], loadComponent: () => import('./pages/overview/overview.component').then(m => m.OverviewComponent) },
      { path: 'coaching', title: 'Coaching', canActivate: [coachingGuard], loadComponent: () => import('./pages/coaching/coaching.component').then(m => m.CoachingComponent) },
      { path: 'audit-log', title: 'Audit log', canActivate: [platformAdminGuard], component: PlaceholderComponent },
      { path: 'feature-flags', title: 'Feature flags', canActivate: [platformAdminGuard], component: PlaceholderComponent },
      { path: 'roles', title: 'Roles and permissions', canActivate: [platformAdminGuard], component: PlaceholderComponent },
      { path: 'revenue', title: 'Platform revenue', canActivate: [platformAdminGuard], component: PlaceholderComponent },
      { path: 'impersonation', title: 'Impersonation', canActivate: [platformAdminGuard], component: PlaceholderComponent },
      {
        path: 'staff-access',
        title: 'Staff access',
        canActivate: [staffGuard('staff.access.manage', 'staff.exceptional.issue')],
        loadComponent: () =>
          import('./pages/staff-access/staff-access.component').then((m) => m.StaffAccessComponent),
      },
      {
        path: 'needs-sorting',
        title: 'Needs sorting',
        canActivate: [staffGuard('staff.exceptional.issue')],
        loadComponent: () =>
          import('./pages/needs-sorting/needs-sorting.component').then((m) => m.NeedsSortingComponent),
      },
      { path: 'api-docs', title: 'API docs', canActivate: [staffGuard('staff.api_docs.read')], component: PlaceholderComponent },
      {
        path: 'my-access',
        title: 'Your access',
        loadComponent: () => import('./pages/my-access/my-access.component').then((m) => m.MyAccessComponent),
      },

      {
        // The backend resolves
        // scope from staff reporting access. Customer reporting is a separate
        // workspace; this is not an organization-admin tool.
        path: 'analytics/analyst',
        title: 'Analyst',
        canActivate: [staffGuard('staff.reports.read')],
        loadComponent: () =>
          import('./pages/analytics/analyst/analyst.component').then((m) => m.AnalystComponent),
      },

      {
        path: 'participants',
        title: 'Users',
        canActivate: [staffGuard('staff.users.read')],
        loadComponent: () =>
          import('./pages/participants/list/participants-list.component').then(
            (m) => m.ParticipantsListComponent,
          ),
      },
      {
        path: 'participants/:userId',
        title: 'Participant',
        canActivate: [staffGuard('staff.users.read')],
        loadComponent: () =>
          import('./pages/participants/detail/participant-detail.component').then(
            (m) => m.ParticipantDetailComponent,
          ),
      },
      {
        path: 'programs',
        title: 'Programs',
        canActivate: [curriculumEditGuard],
        loadComponent: () =>
          import('./pages/programs/list/programs-list.component').then(
            (m) => m.ProgramsListComponent,
          ),
      },
      {
        path: 'programs/flow',
        title: 'Prerequisite map',
        canActivate: [staffGuard('staff.curriculum.edit')],
        loadComponent: () =>
          import('./pages/programs/flow/program-flow.component').then(
            (m) => m.ProgramFlowComponent,
          ),
      },
      {
        path: 'programs/add',
        title: 'Add program',
        canActivate: [staffGuard('staff.curriculum.edit')],
        loadComponent: () =>
          import('./pages/programs/form/program-form.component').then(
            (m) => m.ProgramFormComponent,
          ),
      },
      {
        path: 'programs/:programId/edit',
        title: 'Edit program',
        canActivate: [curriculumEditGuard],
        loadComponent: () =>
          import('./pages/programs/form/program-form.component').then(
            (m) => m.ProgramFormComponent,
          ),
      },
      {
        path: 'programs/:programId/version/:versionNumber',
        title: 'Program version',
        canActivate: [curriculumEditGuard],
        loadComponent: () =>
          import('./pages/programs/version-preview/program-version-preview.component').then(
            (m) => m.ProgramVersionPreviewComponent,
          ),
      },
      {
        path: 'programs/:programId',
        title: 'Program editor',
        canActivate: [curriculumEditGuard],
        loadComponent: () =>
          import('./pages/programs/editor/program-editor.component').then(
            (m) => m.ProgramEditorComponent,
          ),
      },
      {
        path: 'organizations',
        title: 'Organizations',
        loadComponent: () =>
          import('./pages/organizations/list/organizations-list.component').then(
            (m) => m.OrganizationsListComponent,
          ),
      },
      {
        path: 'organizations/new',
        title: 'New organization',
        canActivate: [staffGuard('staff.orgs.manage')],
        loadComponent: () =>
          import('./pages/organizations/form/organization-form.component').then(
            (m) => m.OrganizationFormComponent,
          ),
      },
      {
        path: 'organizations/:orgId',
        title: 'Organization',
        loadComponent: () =>
          import('./pages/organizations/detail/organization-detail.component').then(
            (m) => m.OrganizationDetailComponent,
          ),
      },
      {
        path: 'organizations/:orgId/divisions/new',
        title: 'New division',
        loadComponent: () =>
          import('./pages/organizations/divisions/new/division-form.component').then(
            (m) => m.DivisionFormComponent,
          ),
      },
      {
        path: 'organizations/:orgId/divisions/:divisionId/roster',
        title: 'Transition roster',
        loadComponent: () =>
          import('./pages/organizations/divisions/roster/roster-builder.component').then(
            (m) => m.RosterBuilderComponent,
          ),
      },

      // Tenant-scope destinations on the build list. Ordered most-specific
      // first; `divisions/new` (above) keeps precedence over `divisions/:id`.
      // Note: the `organizations/:orgId` org-detail route above is a leaf route
      // (component, no children), so Angular requires it to consume the FULL
      // URL — it does NOT swallow `/organizations/:orgId/billing` etc. (verified
      // live). These sub-paths therefore resolve here as intended.
      { path: 'organizations/:orgId/divisions/:divisionId/seats', title: 'Participants', loadComponent: () => import('./pages/organizations/detail/organization-detail.component').then(m => m.OrganizationDetailComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/progress', title: 'Participant progress', loadComponent: () => import('./pages/organizations/participants/scoped-progress.component').then(m => m.ScopedProgressComponent) },
      { path: 'organizations/:orgId/progress', title: 'Participant progress', loadComponent: () => import('./pages/organizations/participants/scoped-progress.component').then(m => m.ScopedProgressComponent) },
      { path: 'organizations/:orgId/safe-dossier-review', title: 'Safe dossier review', loadComponent: () => import('./pages/organizations/participants/platform-dossier-review.component').then(m => m.PlatformDossierReviewComponent) },
      { path: 'organizations/:orgId/reports', title: 'Reports and insights', loadComponent: () => import('./pages/organizations/participants/customer-reports.component').then(m => m.CustomerReportsComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/reports', title: 'Reports and insights', loadComponent: () => import('./pages/organizations/participants/customer-reports.component').then(m => m.CustomerReportsComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/participants', redirectTo: 'organizations/:orgId/divisions/:divisionId/seats' },
      { path: 'organizations/:orgId/divisions/:divisionId/programs', title: 'Programs', component: PlaceholderComponent },
      { path: 'organizations/:orgId/divisions/:divisionId/billing', title: 'Billing', loadComponent: () => import('./pages/organizations/divisions/division-home.component').then(m => m.DivisionHomeComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/members', title: 'Team and access', loadComponent: () => import('./pages/organizations/team-access/team-access.component').then((m) => m.TeamAccessComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/settings', title: 'Settings', component: PlaceholderComponent },
      { path: 'organizations/:orgId/divisions/:divisionId/branding', title: 'Branding', loadComponent: () => import('./pages/organizations/branding/branding.component').then(m => m.BrandingComponent) },
      // Org admin alerts (VOFF-502): the Outbox pages, scoped by the route.
      { path: 'organizations/:orgId/divisions/:divisionId/alerts', title: 'Alerts', loadComponent: () => import('./pages/outbox/list/outbox-list.component').then(m => m.OutboxListComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/alerts/new', title: 'New alert', loadComponent: () => import('./pages/outbox/compose/outbox-compose.component').then(m => m.OutboxComposeComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/alerts/:id', title: 'Alert', loadComponent: () => import('./pages/outbox/compose/outbox-compose.component').then(m => m.OutboxComposeComponent) },
      { path: 'organizations/:orgId/alerts', title: 'Alerts', loadComponent: () => import('./pages/outbox/list/outbox-list.component').then(m => m.OutboxListComponent) },
      { path: 'organizations/:orgId/alerts/new', title: 'New alert', loadComponent: () => import('./pages/outbox/compose/outbox-compose.component').then(m => m.OutboxComposeComponent) },
      { path: 'organizations/:orgId/alerts/:id', title: 'Alert', loadComponent: () => import('./pages/outbox/compose/outbox-compose.component').then(m => m.OutboxComposeComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/messages', title: 'Messages', loadComponent: () => import('./pages/organizations/messages/messages.component').then(m => m.MessagesComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/effective', title: 'Effective settings', loadComponent: () => import('./pages/organizations/effective/effective-settings.component').then(m => m.EffectiveSettingsComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId/scout', title: 'Scout', loadComponent: () => import('./pages/organizations/scout-settings/scout-settings.component').then(m => m.ScoutSettingsComponent) },
      { path: 'organizations/:orgId/divisions/:divisionId', title: 'Division', loadComponent: () => import('./pages/organizations/divisions/division-home.component').then(m => m.DivisionHomeComponent) },
      { path: 'organizations/:orgId/divisions', title: 'Divisions', loadComponent: () => import('./pages/organizations/detail/organization-detail.component').then(m => m.OrganizationDetailComponent), data: { section: 'divisions' } },
      {
        path: 'organizations/:orgId/participants/:userId',
        title: 'Participant',
        loadComponent: () =>
          import('./pages/organizations/participants/org-participant-detail.component').then(
            (m) => m.OrgParticipantDetailComponent,
          ),
      },
      // Participants: rosters and their seats and people in one place. The old
      // Rosters / Seats path redirects here with its query params.
      { path: 'organizations/:orgId/participants', title: 'Participants', loadComponent: () => import('./pages/organizations/detail/organization-detail.component').then(m => m.OrganizationDetailComponent), data: { section: 'seats' } },
      { path: 'organizations/:orgId/programs', title: 'Programs', component: PlaceholderComponent },
      { path: 'organizations/:orgId/rosters', redirectTo: 'organizations/:orgId/participants' },
      { path: 'organizations/:orgId/billing', title: 'Billing', loadComponent: () => import('./pages/organizations/detail/organization-detail.component').then(m => m.OrganizationDetailComponent), data: { section: 'billing' } },
      { path: 'organizations/:orgId/members', title: 'Team and access', loadComponent: () => import('./pages/organizations/team-access/team-access.component').then((m) => m.TeamAccessComponent) },
      { path: 'organizations/:orgId/branding', title: 'Branding', loadComponent: () => import('./pages/organizations/branding/branding.component').then(m => m.BrandingComponent) },
      { path: 'organizations/:orgId/messages', title: 'Messages', loadComponent: () => import('./pages/organizations/messages/messages.component').then(m => m.MessagesComponent) },
      { path: 'organizations/:orgId/setup', title: 'Setup', loadComponent: () => import('./pages/organizations/setup/setup.component').then(m => m.SetupComponent) },
      { path: 'organizations/:orgId/scout', title: 'Scout', loadComponent: () => import('./pages/organizations/scout-settings/scout-settings.component').then(m => m.ScoutSettingsComponent) },
      // The old Curriculum page is part of the Program Library now.
      { path: 'organizations/:orgId/curriculum', redirectTo: 'organizations/:orgId/library' },
      ...programLibraryRoutes('organizations/:orgId'),
      ...programLibraryRoutes('organizations/:orgId/divisions/:divisionId'),
      { path: 'organizations/:orgId/settings', title: 'Settings', loadComponent: () => import('./pages/organizations/detail/organization-detail.component').then(m => m.OrganizationDetailComponent), data: { section: 'settings' } },

      {
        path: 'pricing',
        title: 'Pricing',
        canActivate: [staffGuard('staff.catalog.manage')],
        loadComponent: () =>
          import('./pages/pricing/list/pricing-list.component').then(
            (m) => m.PricingListComponent,
          ),
      },
      {
        path: 'pricing/new',
        title: 'New package',
        canActivate: [staffGuard('staff.catalog.manage')],
        loadComponent: () =>
          import('./pages/pricing/form/pricing-form.component').then(
            (m) => m.PricingFormComponent,
          ),
      },
      {
        path: 'pricing/discount-codes',
        title: 'Discount codes',
        canActivate: [staffGuard('staff.discounts')],
        loadComponent: () =>
          import('./pages/pricing/discount-codes/list/discount-code-list.component').then(
            (m) => m.DiscountCodeListComponent,
          ),
      },
      {
        path: 'pricing/discount-codes/new',
        title: 'New discount code',
        canActivate: [staffGuard('staff.discounts')],
        loadComponent: () =>
          import('./pages/pricing/discount-codes/form/discount-code-form.component').then(
            (m) => m.DiscountCodeFormComponent,
          ),
      },
      {
        path: 'pricing/discount-codes/:code',
        title: 'Discount code',
        canActivate: [staffGuard('staff.discounts')],
        loadComponent: () =>
          import('./pages/pricing/discount-codes/form/discount-code-form.component').then(
            (m) => m.DiscountCodeFormComponent,
          ),
      },
      {
        path: 'pricing/:productId/edit',
        title: 'Edit package',
        canActivate: [staffGuard('staff.catalog.manage')],
        loadComponent: () =>
          import('./pages/pricing/form/pricing-form.component').then(
            (m) => m.PricingFormComponent,
          ),
      },
      {
        path: 'outbox',
        title: 'Outbox',
        canActivate: [staffGuard('staff.users.support')],
        loadComponent: () =>
          import('./pages/outbox/list/outbox-list.component').then((m) => m.OutboxListComponent),
      },
      {
        path: 'outbox/new',
        title: 'New message',
        canActivate: [staffGuard('staff.users.support')],
        loadComponent: () =>
          import('./pages/outbox/compose/outbox-compose.component').then((m) => m.OutboxComposeComponent),
      },
      {
        path: 'outbox/:id',
        title: 'Message',
        canActivate: [staffGuard('staff.users.support')],
        loadComponent: () =>
          import('./pages/outbox/compose/outbox-compose.component').then((m) => m.OutboxComposeComponent),
      },
      {
        path: 'feedback',
        title: 'Feedback',
        canActivate: [staffGuard('staff.users.support')],
        loadComponent: () =>
          import('./pages/feedback/list/feedback-list.component').then(
            (m) => m.FeedbackListComponent,
          ),
      },
      {
        path: 'feedback/:id',
        title: 'Feedback item',
        canActivate: [staffGuard('staff.users.support')],
        loadComponent: () =>
          import('./pages/feedback/detail/feedback-detail.component').then(
            (m) => m.FeedbackDetailComponent,
          ),
      },
    ],
  },
  {
    path: 'accept-invite/:token',
    title: 'Accept invitation',
    loadComponent: () =>
      import('./pages/accept-invite/accept-invite.component').then(
        (m) => m.AcceptInviteComponent,
      ),
  },
  { path: '**', redirectTo: '' },
];
